/**
 * @ken/module-fixed-assets — frontend entry point.
 *
 * Story-page migration (ADR-0081/0082, PARTY archetype): the in-list detail
 * Sheet is RETIRED in favour of the full-page STORY route. Clicking a fixed
 * asset row navigates to `fixed-assets/$id/overview`; the parent
 * `fixed-assets/$id` renders the StoryPageDetail shell + routed tab children
 * (overview / related / notes) with an index redirect to overview. Mirrors the
 * customers and products story rollout.
 */

import {
  lazyScreen,
  type AnyRoute,
  type CreateModuleRoutes,
} from "@ken/cockpit-types";
import { isRecordId, RecordNotFound } from "@ken/ui";
import { createRoute, notFound, redirect } from "@tanstack/react-router";

import type { FixedAssetStorySearch } from "./routes/story/fixed-asset-story-detail";

// One loader per screen file, shared by every screen that file exports, so the
// route table carries each file's import (and its preload list) once (SF-BUNDLE).
const loadFixedAssetStoryTabs = () => import("./routes/story/fixed-asset-story-tabs");

// Screens download the first time they are opened (ADR-040, BND-01). The
// route table below stays eager: paths, params, validateSearch, redirects.
const FixedAssetListPage = lazyScreen(
  () => import("./routes/fixed-asset-list"),
  "FixedAssetListPage",
);
const FixedAssetStoryDetail = lazyScreen(
  () => import("./routes/story/fixed-asset-story-detail"),
  "FixedAssetStoryDetail",
);
const FixedAssetActivityRoute = lazyScreen(
  loadFixedAssetStoryTabs,
  "FixedAssetActivityRoute",
);
const FixedAssetNotesRoute = lazyScreen(
  loadFixedAssetStoryTabs,
  "FixedAssetNotesRoute",
);
const FixedAssetOverviewRoute = lazyScreen(
  loadFixedAssetStoryTabs,
  "FixedAssetOverviewRoute",
);
const FixedAssetRelatedRoute = lazyScreen(
  loadFixedAssetStoryTabs,
  "FixedAssetRelatedRoute",
);
export type { FixedAssetListSearch } from "./routes/fixed-asset-list";

// UI-06: fixed assets have no create surface in the cockpit (they arrive
// through the QuickBooks connector), so this takes the "create surface out of
// scope" outcome. This module fired TWO 422s on a literal `new` — the detail
// read and a `graph/related?entity_id=new` — and the guard below stops both.

export const createRoutes: CreateModuleRoutes = (parentRoute: AnyRoute) => {
  const listRoute = createRoute({
    getParentRoute: () => parentRoute,
    path: "fixed-assets",
    validateSearch: (
      raw: Record<string, unknown>,
    ): import("./routes/fixed-asset-list").FixedAssetListSearch => ({
      ...(typeof raw["q"] === "string" && raw["q"] ? { q: raw["q"] } : {}),
      ...(typeof raw["sold_filter"] === "string" && raw["sold_filter"]
        ? {
            sold_filter: raw[
              "sold_filter"
            ] as import("./api/client").FixedAssetSoldFilter,
          }
        : {}),
      ...(typeof raw["filter_status"] === "string" && raw["filter_status"]
        ? {
            filter_status: raw[
              "filter_status"
            ] as import("./api/client").FixedAssetFilterStatus,
          }
        : {}),
      ...(typeof raw["highlight"] === "string" && raw["highlight"]
        ? { highlight: raw["highlight"] }
        : {}),
      // POP-01 (ADR-0229): the record open in the pop-up, so a reload
      // reopens it and the browser's back button closes it.
      ...(typeof raw["open"] === "string" && raw["open"]
        ? { open: raw["open"] }
        : {}),
      // POP-02 (ADR-0230): the pop-up's open tab.
      ...(typeof raw["tab"] === "string" && raw["tab"]
        ? { tab: raw["tab"] }
        : {}),
    }),
    component: FixedAssetListPage,
  });

  // UI-06: a STATIC route for the literal `new`, so the id route below can
  // never match it and fire a detail read for a record called "new".
  const literalNewRoute = createRoute({
    getParentRoute: () => parentRoute,
    path: "fixed-assets/new",
    component: RecordNotFound,
  });

  // ── Story-page detail (ADR-0081): parent fixed-assets/$id shell + routed
  //    tab children + an index redirect to …/overview. ─────────────────────
  const detailRoute = createRoute({
    getParentRoute: () => parentRoute,
    path: "fixed-assets/$id",
    // UI-06's id-shape guard: stops both the detail read and the
    // graph/related read before either is issued.
    beforeLoad: ({ params }) => {
      if (!isRecordId((params as { id?: string }).id)) throw notFound();
    },
    notFoundComponent: RecordNotFound,
    validateSearch: (raw: Record<string, unknown>): FixedAssetStorySearch => ({
      ...(typeof raw["q"] === "string" && raw["q"] ? { q: raw["q"] } : {}),
      ...(typeof raw["sold_filter"] === "string" && raw["sold_filter"]
        ? {
            sold_filter: raw[
              "sold_filter"
            ] as import("./api/client").FixedAssetSoldFilter,
          }
        : {}),
      ...(typeof raw["filter_status"] === "string" && raw["filter_status"]
        ? {
            filter_status: raw[
              "filter_status"
            ] as import("./api/client").FixedAssetFilterStatus,
          }
        : {}),
      ...(typeof raw["from"] === "number"
        ? { from: raw["from"] as number }
        : {}),
    }),
    component: FixedAssetStoryDetail,
  });

  // Index redirect fixed-assets/$id → …/overview (the default tab).
  const detailIndexRoute = createRoute({
    getParentRoute: () => detailRoute,
    path: "/",
    beforeLoad: ({ params }) => {
      throw redirect({
        to: "/$tenantSlug/fixed-assets/$id/overview",
        params: params as never,
        replace: true,
      });
    },
  });

  const overviewRoute = createRoute({
    getParentRoute: () => detailRoute,
    path: "overview",
    component: FixedAssetOverviewRoute,
  });
  const relatedRoute = createRoute({
    getParentRoute: () => detailRoute,
    path: "related",
    component: FixedAssetRelatedRoute,
  });
  const notesRoute = createRoute({
    getParentRoute: () => detailRoute,
    path: "notes",
    component: FixedAssetNotesRoute,
  });
  const activityRoute = createRoute({
    getParentRoute: () => detailRoute,
    path: "activity",
    component: FixedAssetActivityRoute,
  });

  (detailRoute as unknown as { addChildren: (c: unknown) => void }).addChildren(
    [detailIndexRoute, overviewRoute, relatedRoute, notesRoute, activityRoute],
  );

  // Static before parameterised.
  return [listRoute, literalNewRoute, detailRoute];
};
